'use client'

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdArrowBack, MdSave, MdArticle } from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import {
    TemplateCategory,
} from '@/lib/api/templates/template-api'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import {
    createMedicalReport,
    updateMedicalReport,
    getMedicalReport,
    CreateMedicalReportData,
    UpdateMedicalReportData,
    MedicalReportType,
    MEDICAL_REPORT_TYPE_LABELS,
} from '@/lib/api/medical-reports/medical-report-api'
import { ClientSelector } from '@/components/client/ClientSelector'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import RichTextEditor from '@/components/rich-text-editor'
import SaveAsTemplateModal from '@/components/medical/save-as-template-modal'
import TemplatePicker from '@/components/medical/template-picker'
import '@/components/rich-text-editor/styles.css'

interface FormData {
    title: string
    type: MedicalReportType
    content: string
    summary: string
    conclusion: string
    clientId: string
    templateId: string
    /**
     * Quem ASSINA o laudo — não quem o digita.
     *
     * A secretária monta pelo médico, como já fazia com o atestado. Quem está logado
     * como profissional tem o campo preenchido e nem o vê.
     */
    professionalId: string
}

export default function ReportFormPage() {
    const { user, isProfessional, isPartner, isEmployee } = useAuth()
    const hidePartnerSelector = isPartner || (isEmployee && !!user?.partnerId)
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(false)
    /**
     * Laudo recém-criado cujo texto ainda pode virar modelo.
     *
     * Guarda também para onde ir depois: o convite SEGURA a navegação, senão o
     * modal apareceria e sumiria junto com a tela no mesmo instante.
     */
    const [pendingTemplate, setPendingTemplate] = useState<{
        content: string
        professionalId: number | null
        nextPath: string
    } | null>(null)
    const [partnerId, setPartnerId] = useState<number | null>(
        hidePartnerSelector ? user?.partnerId || null : null
    )

    const [formData, setFormData] = useState<FormData>({
        title: '',
        type: 'CONSULTATION',
        content: '',
        summary: '',
        conclusion: '',
        clientId: '',
        templateId: '',
        professionalId: '',
    })

    useEffect(() => {
        if (user?.companyId) {
            if (isEditing && id) {
                loadReport()
            } else if (isProfessional && user?.professionalId) {
                // O médico logado não escolhe a si mesmo a cada laudo.
                setFormData(prev => ({
                    ...prev,
                    professionalId: String(user.professionalId),
                }))
            }
        }
    }, [id, isEditing, user?.companyId, isProfessional, user?.professionalId])

    const loadReport = async () => {
        if (!id) return

        setLoadingData(true)
        try {
            const response = await getMedicalReport(parseInt(id, 10))
            if (response.success && response.result) {
                const report = response.result
                setFormData({
                    title: report.title || '',
                    type: report.type || 'CONSULTATION',
                    content: report.content || '',
                    summary: report.summary || '',
                    conclusion: report.conclusion || '',
                    clientId: report.clientId?.toString() || '',
                    templateId: report.templateId?.toString() || '',
                    professionalId: report.professionalId?.toString() || '',
                })
                if (report.partnerId) {
                    setPartnerId(report.partnerId)
                }
            } else {
                toast.error(response.error || 'Erro ao carregar laudo')
                navigate('/bookings/reports')
            }
        } catch (error) {
            toast.error('Erro ao carregar laudo')
            navigate('/bookings/reports')
        } finally {
            setLoadingData(false)
        }
    }

    const updateFormField = (field: keyof FormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    /** Texto que veio de modelo não é convidado a virar modelo de novo. */
    const usedTemplateRef = useRef(false)

    /**
     * O modelo entra com `{{paciente}}` JÁ trocado pelo nome de quem está sendo
     * atendido — quem resolve é o backend, no `apply`. Antes disso a chave crua
     * seguia até o PDF entregue ao paciente.
     */
    const handleTemplateApply = (applied: {
        templateId: number
        content: string | null
    }) => {
        usedTemplateRef.current = true
        setFormData(prev => ({
            ...prev,
            templateId: String(applied.templateId),
            content: applied.content || '',
        }))
    }

    const validateForm = (): boolean => {
        if (!formData.title.trim()) {
            toast.error('Título é obrigatório')
            return false
        }
        // Verifica se o conteúdo HTML tem texto (remove tags HTML para validação)
        const contentText = formData.content.replace(/<[^>]*>/g, '').trim()
        if (!contentText) {
            toast.error('Conteúdo é obrigatório')
            return false
        }
        if (!formData.clientId) {
            toast.error('Selecione um paciente')
            return false
        }
        if (!formData.professionalId) {
            toast.error('Selecione o profissional que assina o laudo')
            return false
        }
        return true
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) return
        if (!user?.companyId) {
            toast.error('Usuário não autenticado')
            return
        }

        setLoading(true)
        try {
            if (isEditing && id) {
                const updateData: UpdateMedicalReportData = {
                    title: formData.title.trim(),
                    type: formData.type,
                    content: formData.content.trim(),
                    summary: formData.summary.trim() || undefined,
                    conclusion: formData.conclusion.trim() || undefined,
                }

                const response = await updateMedicalReport(
                    parseInt(id, 10),
                    updateData
                )
                if (response.success) {
                    toast.success('Laudo atualizado com sucesso!')
                    navigate(`/bookings/reports/${id}`)
                } else {
                    toast.error(response.error || 'Erro ao atualizar laudo')
                }
            } else {
                const createData: CreateMedicalReportData = {
                    title: formData.title.trim(),
                    type: formData.type,
                    content: formData.content.trim(),
                    summary: formData.summary.trim() || undefined,
                    conclusion: formData.conclusion.trim() || undefined,
                    clientId: parseInt(formData.clientId, 10),
                    professionalId: parseInt(formData.professionalId, 10),
                    partnerId: partnerId || undefined,
                    companyId: user.companyId,
                    templateId: formData.templateId
                        ? parseInt(formData.templateId, 10)
                        : undefined,
                }

                const response = await createMedicalReport(createData)
                if (response.success && response.result) {
                    toast.success('Laudo criado com sucesso!')
                    const nextPath = `/bookings/reports/${response.result.id}`
                    if (
                        !usedTemplateRef.current &&
                        createData.content?.trim()
                    ) {
                        setPendingTemplate({
                            content: createData.content,
                            professionalId: createData.professionalId,
                            nextPath,
                        })
                    } else {
                        navigate(nextPath)
                    }
                } else {
                    toast.error(response.error || 'Erro ao criar laudo')
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar laudo')
        } finally {
            setLoading(false)
        }
    }

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando dados do laudo...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/bookings/reports')}
                    className="btn btn-ghost btn-circle btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
                        <MdArticle className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            {isEditing ? 'Editar Laudo' : 'Novo Laudo'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize as informações do laudo'
                                : 'Preencha os dados do novo laudo'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Informações do Laudo
                        </h2>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Título <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Título do laudo"
                                value={formData.title}
                                onChange={e =>
                                    updateFormField('title', e.target.value)
                                }
                                required
                            />
                        </div>

                        <div className="form-control w-full mt-4">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Paciente{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <ClientSelector
                                value={
                                    formData.clientId
                                        ? Number(formData.clientId)
                                        : null
                                }
                                onChange={id =>
                                    updateFormField(
                                        'clientId',
                                        id ? String(id) : ''
                                    )
                                }
                                label=""
                                placeholder="Selecione..."
                                disabled={isEditing}
                            />
                        </div>

                        {/* Quem ASSINA. Escondido para o próprio médico, que já vem
                            preenchido: um campo com uma opção só é ruído. */}
                        {!isProfessional && (
                            <div className="form-control w-full mt-4">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Profissional{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <ProfessionalSearchSelector
                                    value={
                                        formData.professionalId
                                            ? Number(formData.professionalId)
                                            : null
                                    }
                                    onChange={id =>
                                        updateFormField(
                                            'professionalId',
                                            id ? String(id) : ''
                                        )
                                    }
                                    label=""
                                    placeholder="Quem assina este laudo..."
                                    disabled={isEditing}
                                />
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Tipo
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.type}
                                    onChange={e =>
                                        updateFormField(
                                            'type',
                                            e.target.value as MedicalReportType
                                        )
                                    }
                                >
                                    {(
                                        Object.keys(
                                            MEDICAL_REPORT_TYPE_LABELS
                                        ) as MedicalReportType[]
                                    ).map(type => (
                                        <option key={type} value={type}>
                                            {MEDICAL_REPORT_TYPE_LABELS[type]}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Precisa do paciente: o modelo entra com o nome
                                dele já no texto. */}
                            {!isEditing && formData.clientId && (
                                <TemplatePicker
                                    category={TemplateCategory.REPORT}
                                    clientId={parseInt(formData.clientId, 10)}
                                    professionalId={
                                        formData.professionalId
                                            ? parseInt(
                                                  formData.professionalId,
                                                  10
                                              )
                                            : null
                                    }
                                    label="Modelo"
                                    onApply={handleTemplateApply}
                                />
                            )}
                        </div>

                        {!hidePartnerSelector && (
                            <div className="mt-4">
                                <PartnerSelector
                                    value={partnerId}
                                    onChange={id => setPartnerId(id)}
                                    label="Parceiro (opcional)"
                                />
                            </div>
                        )}

                        <div className="form-control w-full mt-4">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Resumo
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Resumo do laudo..."
                                value={formData.summary}
                                onChange={e =>
                                    updateFormField('summary', e.target.value)
                                }
                                rows={2}
                            />
                        </div>

                        <div className="form-control w-full mt-4">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Conteúdo{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <RichTextEditor
                                content={formData.content}
                                onChange={content =>
                                    updateFormField('content', content)
                                }
                                placeholder="Digite o conteúdo do laudo..."
                                minHeight="500px"
                            />
                        </div>

                        <div className="form-control w-full mt-4">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Conclusão
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Conclusão do laudo..."
                                value={formData.conclusion}
                                onChange={e =>
                                    updateFormField(
                                        'conclusion',
                                        e.target.value
                                    )
                                }
                                rows={3}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => navigate('/bookings/reports')}
                        className="btn btn-ghost"
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="loading loading-spinner loading-sm"></span>
                                Salvando...
                            </>
                        ) : (
                            <>
                                <MdSave className="text-lg" />
                                Salvar
                            </>
                        )}
                    </button>
                </div>
            </form>

            {pendingTemplate && (
                <SaveAsTemplateModal
                    isOpen
                    onClose={() => {
                        const next = pendingTemplate.nextPath
                        setPendingTemplate(null)
                        navigate(next)
                    }}
                    category={TemplateCategory.REPORT}
                    content={pendingTemplate.content}
                    professionalId={pendingTemplate.professionalId}
                    suggestedName={formData.title}
                />
            )}
        </div>
    )
}
